/**
 * Shared workflow stage-metadata arrays.
 *
 * These were historically declared inline in `App.tsx`. `servicesStages` is
 * extracted here verbatim so a regression test can lock it (mirroring how GPC
 * exports `GPC_FORM_STAGE_ORDER` from `api/gpcFunctions.ts`), and
 * `complexServicesStages` is added for the Complex Services workflow — its
 * order MUST match `COMPLEX_SERVICES_STAGE_ORDER` in
 * `api/complexServicesFunctions.ts` (the stepper renders this array in order and
 * greys the inapplicable stages in place).
 */

import { COMPLEX_SERVICES_STAGE_ORDER } from '../api/complexServicesFunctions';

export interface StageMeta {
  id: string;
  name: string;
  description: string;
}

/**
 * Services workflow stages (7 stages). MOVED verbatim from `App.tsx` — do not
 * change without updating `servicesFlowUnchanged.test.ts` (regression lock).
 */
export const servicesStages: StageMeta[] = [
  { id: 'services-sow-upload', name: 'PWS Upload', description: 'Upload Performance Work Statement and source documents for services contract' },
  { id: 'services-context-questions', name: 'Context Questions', description: 'Answer context questions about the PWS to improve analysis' },
  { id: 'services-suggestions', name: 'Suggestions', description: 'Review suggestions and select changes to apply. You can make manual edits in the next stage.' },
  { id: 'services-manual-edit', name: 'Manual Edit', description: 'Edit and finalize PWS document. Returning to the Suggestions stage will overwrite all manual edits.' },
  { id: 'services-salient-chars', name: 'Description of Services', description: 'Extract and review the description of services from the PWS' },
  { id: 'services-code-selection', name: 'Code Selection', description: 'Select NAICS and PSC codes for vendor discovery' },
  { id: 'services-vendor-discovery', name: 'Vendor Discovery', description: 'Discover vendors based on description of services and codes' },
];

/** Post-award workflow stages, in canonical flow-storage order (1000–1002). */
export const postAwardStages: StageMeta[] = [
  {
    id: 'post-award-intake',
    name: 'Contract & Package Intake',
    description: 'Select a contract record or save a draft contract package for post-award support.',
  },
  {
    id: 'post-award-recommendation',
    name: 'Guided Action Recommendation',
    description: 'Describe the need and review the recommended post-award action.',
  },
  {
    id: 'post-award-workspace',
    name: 'Draft-only Action Workspace',
    description: 'Prepare a draft-only action workspace for agency review.',
  },
];

/**
 * Complex Services workflow stages (20 stages — the FULL master order). The
 * dynamic stepper renders every stage; `complexDisabledStageIndices` greys the
 * ones that do not apply to the buy. Reused authoring stages 600–606 keep the
 * Services labels; complex-only stages 607–619 follow.
 *
 * Order is load-bearing and MUST equal `COMPLEX_SERVICES_STAGE_ORDER`.
 */
const COMPLEX_STAGE_META: Record<(typeof COMPLEX_SERVICES_STAGE_ORDER)[number], StageMeta> = {
  // Reused Services authoring stages (600–606).
  sow_upload: { id: 'complex-services-sow-upload', name: 'PWS Upload', description: 'Upload Performance Work Statement and source documents' },
  context_questions: { id: 'complex-services-context-questions', name: 'Context Questions', description: 'Answer context questions about the PWS to improve analysis' },
  suggestions: { id: 'complex-services-suggestions', name: 'Suggestions', description: 'Review suggestions and select changes to apply' },
  manual_edit: { id: 'complex-services-manual-edit', name: 'Manual Edit', description: 'Edit and finalize the PWS document' },
  salient_chars: { id: 'complex-services-salient-chars', name: 'Description of Services', description: 'Extract and review the description of services from the PWS' },
  code_selection: { id: 'complex-services-code-selection', name: 'Code Selection', description: 'Select NAICS and PSC codes for vendor discovery' },
  vendor_discovery: { id: 'complex-services-vendor-discovery', name: 'Vendor Discovery', description: 'Discover vendors based on description of services and codes' },
  // Complex-only stages (607–619), in the required displayed order.
  mrr: { id: 'complex-services-mrr', name: 'Market Research Report', description: 'Review and retain the market research report artifact' },
  ige: { id: 'complex-services-ige', name: 'IGE / IGCE', description: 'Generate the Independent Government Estimate from CLINs and labor rates' },
  igd: { id: 'complex-services-igd', name: 'IGD Checklist', description: 'Review and retain the inherently governmental determination artifact' },
  compliance: { id: 'complex-services-compliance', name: 'Compliance', description: 'Wage determination and insurance compliance checks' },
  pws_diff: { id: 'complex-services-pws-diff', name: 'PWS Diff', description: 'Upload and diff the old PWS; inherently-governmental check' },
  options: { id: 'complex-services-options', name: 'Option Periods', description: 'Define the base + option-year structure and continuing-need justification' },
  reqval: { id: 'complex-services-reqval', name: 'Requirements Validation', description: 'Generate the IGD checklist, SCMRV, FTE breakout, and RAD' },
  qasp: { id: 'complex-services-qasp', name: 'QASP', description: 'Generate the Quality Assurance Surveillance Plan' },
  dd2579: { id: 'complex-services-dd2579', name: 'DD 2579', description: 'Complete the DD 2579 small-business coordination form' },
  form9: { id: 'complex-services-form9', name: 'AF Form 9 / Purchase Request', description: 'Review and retain the purchase request and funding artifact' },
  solicitation: { id: 'complex-services-solicitation', name: 'Solicitation', description: 'Assemble the solicitation combo and CLS clause matrix' },
  clearance: { id: 'complex-services-clearance', name: 'Clearance Reviews', description: 'Legal, business, and contract clearance reviews' },
  record: { id: 'complex-services-record', name: 'Contract Record', description: 'Capture the contract record and export the tracker hand-off' },
};

export const complexServicesStages: StageMeta[] = COMPLEX_SERVICES_STAGE_ORDER.map(
  (key) => COMPLEX_STAGE_META[key],
);
